import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Shield, Clock, CheckCircle, XCircle, AlertTriangle, Send,
  Loader2, FileText, Trash2, Eye, RefreshCw, ExternalLink, ChevronDown, ChevronUp
} from "lucide-react";
import { Card } from "../shared/UIComponents";
import EntityPreviewLink from "../shared/EntityPreviewLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending: { bg: "bg-amber-50 border-amber-200", text: "text-amber-700", icon: Clock, label: "Pending" },
  approved: { bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-700", icon: CheckCircle, label: "Approved" },
  executed: { bg: "bg-blue-50 border-blue-200", text: "text-blue-700", icon: Shield, label: "Executed" },
  rejected: { bg: "bg-red-50 border-red-200", text: "text-red-700", icon: XCircle, label: "Rejected" },
  cancelled: { bg: "bg-gray-50 border-gray-200", text: "text-gray-500", icon: Trash2, label: "Cancelled" },
};

const RISK_STYLES = {
  critical: "bg-red-100 text-red-700 border-red-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-green-100 text-green-700 border-green-200",
};

// Map entity_type to navigation type for 360 pages
const ENTITY_NAV_MAP = {
  opportunity: "deal",
  deal: "deal",
  fund: "fund",
  investor: "investor",
  investor_profile: "investor",
  borrower: "borrower",
  lender: "lender",
};

function EntityLink({ entityType, entityId, onEntityNavigate }) {
  const navType = ENTITY_NAV_MAP[entityType];
  if (!navType || !entityId || !onEntityNavigate) {
    return <span className="font-mono text-[#444] text-xs">{entityType}/{entityId}</span>;
  }
  return (
    <EntityPreviewLink
      entityType={entityType}
      entityId={entityId}
      onNavigate={() => onEntityNavigate(navType, entityId)}
    />
  );
}

function PayloadDetails({ payload }) {
  if (!payload || Object.keys(payload).length === 0) return null;
  return (
    <div className="mt-2 p-2.5 rounded-lg bg-white/80 border border-[#e8e0d0]">
      <p className="text-[9px] font-bold uppercase text-[#999] tracking-wider mb-1.5">Requested Changes</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        {Object.entries(payload).map(([k, v]) => (
          <div key={k} className="flex items-baseline gap-1.5">
            <span className="text-[10px] text-[#999]">{k.replace(/_/g, " ")}:</span>
            <span className="text-[11px] font-semibold text-[#333]">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MakerCheckerPanel({ onEntityNavigate }) {
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [newReq, setNewReq] = useState({ action: "", entity_id: "", entity_type: "", justification: "" });
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingRes, historyRes, configRes] = await Promise.all([
        axios.get(`${API}/maker-checker/pending`, { headers }),
        axios.get(`${API}/maker-checker/history`, { headers }),
        axios.get(`${API}/maker-checker/config`, { headers }),
      ]);
      setPending(pendingRes.data.requests || []);
      setHistory(historyRes.data.requests || []);
      setConfig(configRes.data.actions || {});
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newReq.action || !newReq.entity_id || !newReq.justification) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/maker-checker/submit`, newReq, { headers });
      setNewReq({ action: "", entity_id: "", entity_type: "", justification: "" });
      setSubmitOpen(false);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || "Failed to submit request"); }
    finally { setSubmitting(false); }
  };

  const handleReview = async (mcId, decision) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/maker-checker/${mcId}/review`, { decision, review_notes: reviewNotes }, { headers });
      setReviewingId(null);
      setReviewNotes("");
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || "Review failed"); }
    finally { setSubmitting(false); }
  };

  const handleCancel = async (mcId) => {
    try {
      await axios.delete(`${API}/maker-checker/${mcId}`, { headers });
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || "Cancel failed"); }
  };

  const stats = {
    pending: pending.length,
    approved: history.filter(h => h.status === "approved").length,
    executed: history.filter(h => h.status === "executed").length,
    rejected: history.filter(h => h.status === "rejected").length,
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;

  return (
    <div className="space-y-6" data-testid="maker-checker-panel">
      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Pending Approval", value: stats.pending, icon: Clock, color: "text-amber-600", bg: "bg-amber-50 border-amber-200" },
          { label: "Approved (Awaiting)", value: stats.approved, icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
          { label: "Executed", value: stats.executed, icon: Shield, color: "text-blue-600", bg: "bg-blue-50 border-blue-200" },
          { label: "Rejected", value: stats.rejected, icon: XCircle, color: "text-red-600", bg: "bg-red-50 border-red-200" },
        ].map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <Card hover={false}>
              <div className="flex items-center gap-3" data-testid={`mc-stat-${i}`}>
                <div className={`rounded-xl p-2.5 border ${s.bg}`}><s.icon className={`h-5 w-5 ${s.color}`} /></div>
                <div>
                  <div className="text-2xl font-bold text-[#0A0A0A]">{s.value}</div>
                  <div className="text-[10px] text-[#888] uppercase tracking-wider">{s.label}</div>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Tabs + Submit Button */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {[
            { id: "pending", label: `Pending (${stats.pending})`, icon: Clock },
            { id: "history", label: "History", icon: FileText },
            { id: "config", label: "Protected Actions", icon: Shield },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`mc-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${
                tab === t.id ? "bg-emerald-700 text-white" : "bg-[#faf7f2] text-[#666] border border-[#e8e0d0] hover:border-emerald-300"
              }`}>
              <t.icon className="h-3.5 w-3.5" />{t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={fetchData} className="px-3 py-2 rounded-lg border border-[#e8e0d0] text-[#666] hover:border-emerald-300 text-xs"
            data-testid="mc-refresh">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => setSubmitOpen(!submitOpen)} data-testid="mc-submit-toggle"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-700 text-white text-xs font-medium hover:bg-emerald-600 transition-colors">
            <Send className="h-3.5 w-3.5" /> New Request
          </button>
        </div>
      </div>

      {/* Submit Form */}
      <AnimatePresence>
        {submitOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
            <Card hover={false}>
              <h3 className="text-sm font-bold text-[#222] mb-4" data-testid="mc-submit-form-title">Submit Maker-Checker Request</h3>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-bold uppercase text-[#999] mb-1 block">Action</label>
                    <select value={newReq.action} onChange={e => setNewReq(p => ({ ...p, action: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg border border-[#e8e0d0] text-sm bg-white focus:border-emerald-400 outline-none"
                      data-testid="mc-action-select">
                      <option value="">Select action...</option>
                      {Object.entries(config).map(([key, val]) => (
                        <option key={key} value={key}>{val.label || key}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase text-[#999] mb-1 block">Entity ID</label>
                    <input type="text" value={newReq.entity_id} onChange={e => setNewReq(p => ({ ...p, entity_id: e.target.value }))}
                      placeholder="e.g. opportunity_id, fund_id..."
                      className="w-full px-3 py-2 rounded-lg border border-[#e8e0d0] text-sm focus:border-emerald-400 outline-none"
                      data-testid="mc-entity-id-input" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-[#999] mb-1 block">Entity Type</label>
                  <input type="text" value={newReq.entity_type} onChange={e => setNewReq(p => ({ ...p, entity_type: e.target.value }))}
                    placeholder="e.g. opportunity, fund, investor..."
                    className="w-full px-3 py-2 rounded-lg border border-[#e8e0d0] text-sm focus:border-emerald-400 outline-none"
                    data-testid="mc-entity-type-input" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-[#999] mb-1 block">Justification</label>
                  <textarea value={newReq.justification} onChange={e => setNewReq(p => ({ ...p, justification: e.target.value }))}
                    placeholder="Explain why this action is needed..."
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg border border-[#e8e0d0] text-sm focus:border-emerald-400 outline-none resize-none"
                    data-testid="mc-justification-input" />
                </div>
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => setSubmitOpen(false)}
                    className="px-4 py-2 rounded-lg border border-[#e8e0d0] text-xs text-[#666] hover:bg-[#faf7f2]">Cancel</button>
                  <button type="submit" disabled={submitting || !newReq.action || !newReq.entity_id || !newReq.justification}
                    className="px-4 py-2 rounded-lg bg-emerald-700 text-white text-xs font-medium hover:bg-emerald-600 disabled:opacity-40"
                    data-testid="mc-submit-btn">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Request"}
                  </button>
                </div>
              </form>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pending Tab */}
      {tab === "pending" && (
        <div className="space-y-3" data-testid="mc-pending-list">
          {pending.length === 0 ? (
            <Card hover={false}>
              <div className="text-center py-12">
                <CheckCircle className="h-10 w-10 text-emerald-300 mx-auto mb-3" />
                <p className="text-sm text-[#888]">No pending requests. All clear!</p>
              </div>
            </Card>
          ) : pending.map((mc, i) => {
            const actionLabel = config[mc.action]?.label || mc.action;
            const isReviewing = reviewingId === mc.mc_id;
            const isExpanded = expandedId === mc.mc_id;
            const riskStyle = RISK_STYLES[mc.risk_level] || RISK_STYLES.medium;
            return (
              <motion.div key={mc.mc_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <Card hover={false}>
                  <div data-testid={`mc-pending-${i}`}>
                    {/* Header row */}
                    <div className="flex items-start gap-4">
                      <div className="rounded-xl bg-amber-50 border border-amber-200 p-2.5 mt-0.5">
                        <AlertTriangle className="h-5 w-5 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-bold text-[#222]">{actionLabel}</span>
                          <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-200">PENDING</span>
                          <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold border ${riskStyle}`}>
                            {(mc.risk_level || "medium").toUpperCase()} RISK
                          </span>
                        </div>

                        {/* Clickable entity link */}
                        <div className="text-xs text-[#666] mb-1 flex items-center gap-1.5">
                          <span className="text-[#999]">Entity:</span>
                          <EntityLink entityType={mc.entity_type} entityId={mc.entity_id} onEntityNavigate={onEntityNavigate} />
                        </div>

                        <div className="text-xs text-[#888] italic">"{mc.justification}"</div>

                        {/* Expand/Collapse for details */}
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : mc.mc_id)}
                          className="mt-2 flex items-center gap-1 text-[10px] text-emerald-700 font-semibold hover:text-emerald-900 transition-colors"
                          data-testid={`mc-details-toggle-${i}`}
                        >
                          {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          {isExpanded ? "Hide Details" : "View Request Details"}
                        </button>

                        {/* Expandable details */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                              <PayloadDetails payload={mc.payload} />
                              <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[10px] p-2.5 rounded-lg bg-[#faf7f2] border border-[#e8e0d0]">
                                <div><span className="text-[#999]">Requested by:</span> <span className="text-[#444] font-semibold">{mc.maker_name || mc.maker_user_id}</span></div>
                                <div><span className="text-[#999]">Role:</span> <span className="text-[#444] font-semibold">{mc.maker_role || "—"}</span></div>
                                <div><span className="text-[#999]">Submitted:</span> <span className="text-[#444]">{mc.created_at ? new Date(mc.created_at).toLocaleString() : "—"}</span></div>
                                <div><span className="text-[#999]">Approvers:</span> <span className="text-[#444] font-semibold">{(mc.checker_roles_allowed || []).join(", ") || "admin"}</span></div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* Action Buttons */}
                        {isReviewing ? (
                          <div className="mt-3 p-3 rounded-lg bg-[#faf7f2] border border-[#e8e0d0] space-y-2">
                            <p className="text-[10px] font-bold uppercase text-[#999] tracking-wider">Review Decision</p>
                            <textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)}
                              placeholder="Add review notes (optional)..."
                              rows={2}
                              className="w-full px-3 py-2 rounded-lg border border-[#e8e0d0] text-xs focus:border-emerald-400 outline-none resize-none bg-white"
                              data-testid="mc-review-notes" />
                            <div className="flex gap-2">
                              <button onClick={() => handleReview(mc.mc_id, "approved")} disabled={submitting}
                                className="flex items-center gap-1 px-4 py-2 rounded-lg bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-600 disabled:opacity-40 transition-colors"
                                data-testid="mc-approve-btn">
                                <CheckCircle className="h-3.5 w-3.5" /> Approve
                              </button>
                              <button onClick={() => handleReview(mc.mc_id, "rejected")} disabled={submitting}
                                className="flex items-center gap-1 px-4 py-2 rounded-lg bg-red-600 text-white text-xs font-bold hover:bg-red-500 disabled:opacity-40 transition-colors"
                                data-testid="mc-reject-btn">
                                <XCircle className="h-3.5 w-3.5" /> Reject
                              </button>
                              <button onClick={() => { setReviewingId(null); setReviewNotes(""); }}
                                className="px-4 py-2 rounded-lg border border-[#e8e0d0] text-xs text-[#666] hover:bg-white transition-colors">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-3 flex gap-2">
                            {ENTITY_NAV_MAP[mc.entity_type] && onEntityNavigate && (
                              <button onClick={() => onEntityNavigate(ENTITY_NAV_MAP[mc.entity_type], mc.entity_id)}
                                data-testid={`mc-view-entity-${i}`}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-200 text-blue-700 text-xs font-medium hover:bg-blue-50 transition-colors">
                                <ExternalLink className="h-3.5 w-3.5" /> View Entity
                              </button>
                            )}
                            <button onClick={() => { setReviewingId(mc.mc_id); setExpandedId(mc.mc_id); }}
                              data-testid={`mc-review-${i}`}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-300 text-emerald-700 text-xs font-bold hover:bg-emerald-50 transition-colors">
                              <Eye className="h-3.5 w-3.5" /> Review & Decide
                            </button>
                            <button onClick={() => handleCancel(mc.mc_id)} data-testid={`mc-cancel-${i}`}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-xs hover:bg-red-50 transition-colors">
                              <Trash2 className="h-3 w-3" /> Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* History Tab */}
      {tab === "history" && (
        <div className="space-y-2" data-testid="mc-history-list">
          {history.length === 0 ? (
            <Card hover={false}>
              <div className="text-center py-12">
                <FileText className="h-10 w-10 text-[#ddd] mx-auto mb-3" />
                <p className="text-sm text-[#888]">No history yet.</p>
              </div>
            </Card>
          ) : history.map((mc, i) => {
            const s = STATUS_STYLES[mc.status] || STATUS_STYLES.pending;
            const actionLabel = config[mc.action]?.label || mc.action;
            return (
              <motion.div key={mc.mc_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}>
                <div className={`rounded-xl border p-4 ${s.bg}`} data-testid={`mc-history-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <s.icon className={`h-4 w-4 ${s.text}`} />
                      <span className="text-sm font-bold text-[#222]">{actionLabel}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${s.text} border ${s.bg}`}>{s.label}</span>
                    </div>
                    <span className="text-[10px] text-[#999]">{mc.created_at ? new Date(mc.created_at).toLocaleString() : ""}</span>
                  </div>
                  <div className="text-xs text-[#666] flex items-center gap-1.5">
                    <span className="text-[#999]">Entity:</span>
                    <EntityLink entityType={mc.entity_type} entityId={mc.entity_id} onEntityNavigate={onEntityNavigate} />
                  </div>
                  <div className="text-xs text-[#888] italic mt-1">"{mc.justification}"</div>
                  <div className="flex items-center gap-4 text-[10px] text-[#bbb] mt-2">
                    <span>Maker: {mc.maker_name || mc.maker_user_id}</span>
                    {mc.checker_name && <span>Checker: {mc.checker_name}</span>}
                    {mc.reviewed_at && <span>Reviewed: {new Date(mc.reviewed_at).toLocaleString()}</span>}
                    {mc.executed_at && <span>Executed: {new Date(mc.executed_at).toLocaleString()}</span>}
                  </div>
                  {mc.review_notes && (
                    <div className="mt-2 text-xs text-[#555] bg-white/60 rounded-lg px-3 py-2 border border-white/40">
                      Review: "{mc.review_notes}"
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Config Tab */}
      {tab === "config" && (
        <Card hover={false}>
          <h3 className="text-sm font-bold text-[#222] mb-4 uppercase tracking-wider" data-testid="mc-config-title">Protected Actions</h3>
          <p className="text-xs text-[#888] mb-4">These actions require dual approval through the maker-checker system before execution.</p>
          <div className="space-y-2">
            {Object.entries(config).map(([key, val]) => (
              <div key={key} className="flex items-center gap-3 p-3 rounded-lg bg-[#faf7f2] border border-[#e8e0d0]" data-testid={`mc-config-${key}`}>
                <Shield className="h-4 w-4 text-emerald-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#222]">{val.label || key}</div>
                  <div className="text-[10px] text-[#999]">{val.description || "No description"}</div>
                </div>
                <span className="px-2 py-0.5 rounded text-[9px] font-mono text-[#666] bg-white border border-[#e8e0d0]">{key}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
